import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useTheme } from "../context/ThemeContext";

import StatCard from "../components/StatCard";
import BarChartCard from "../components/BarChartCard";
import CampaignPerformance from "../components/CampaignPerformance";
import OverallReports from "../components/OverallReports";
import CampaignRequest from "../components/CampaignRequest";

import { DollarSign, Users, Percent, UserCheck } from "lucide-react";

const API_BASE_URL = process.env.REACT_APP_BASE_URL;

const Dashboard = () => {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { isDarkMode } = useTheme();

  useEffect(() => {
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) {
      navigate("/login");
      return;
    }

    const fetchDashboardData = async () => {
      try {
        setLoading(true);

        const api = axios.create({
          baseURL: API_BASE_URL,
          headers: { Authorization: `Bearer ${accessToken}` },
        });

        const [dashboardResponse, campaignsResponse] = await Promise.all([
          api.get("/admin/dashboard"),
          api.get("/admin/merchants/campaigns?limit=5"),
        ]);

        if (
          dashboardResponse.data.statusCode === 401 ||
          dashboardResponse.data.message === "Unauthorized"
        ) {
          localStorage.removeItem("accessToken");
          localStorage.removeItem("refreshToken");
          localStorage.removeItem("user");
          window.location.href = "/login";
          return;
        }

        if (
          campaignsResponse.data.statusCode === 401 ||
          campaignsResponse.data.message === "Unauthorized"
        ) {
          localStorage.removeItem("accessToken");
          localStorage.removeItem("refreshToken");
          localStorage.removeItem("user");
          window.location.href = "/login";
          return;
        }

        setDashboardData(dashboardResponse.data);
        setCampaigns(campaignsResponse.data.campaigns || []);
      } catch (err) {
        if (
          err.response?.status === 401 ||
          err.response?.data?.message === "Unauthorized"
        ) {
          localStorage.removeItem("accessToken");
          localStorage.removeItem("refreshToken");
          localStorage.removeItem("user");
          window.location.href = "/login";
          return;
        }

        console.error("Failed to fetch dashboard data:", err);
        const errorMessage =
          err.response?.data?.message || "Failed to load dashboard data";
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [navigate]);

  const handleViewAllCampaigns = () => {
    navigate("/campaigns", { state: { filter: "Pending" } });
  };

  if (loading) {
    return (
      <div
        className={`flex flex-col min-h-screen ${
          isDarkMode ? "bg-black" : "bg-gray-100"
        }`}
      >
        <div
          className={`flex-grow flex items-center justify-center ${
            isDarkMode ? "text-white" : "text-gray-900"
          }`}
        >
          <p className="text-xl">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen ${
        isDarkMode ? "bg-black text-white" : "bg-gray-100 text-gray-900"
      }`}
    >
      <div className="flex flex-col gap-6 p-6">
        {error && (
          <div className="bg-red-900/50 border border-red-500 rounded-xl p-4 text-red-200">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          <StatCard
            title="Total Users"
            value={dashboardData?.users?.total?.toLocaleString() || "0"}
            details={[
              `${dashboardData?.users?.active || 0} Active`,
              `${dashboardData?.users?.inactive || 0} Inactive`,
            ]}
            icon={<Users size={24} className="text-orange-400" />}
          />
          <StatCard
            title="Total Posts"
            value={dashboardData?.posts?.total?.toLocaleString() || "0"}
            details={[
              `${dashboardData?.posts?.published || 0} Published`,
              `${dashboardData?.posts?.draft || 0} Draft`,
            ]}
            icon={<DollarSign size={24} className="text-orange-400" />}
          />
          <StatCard
            title="Active Users Rate"
            value={
              dashboardData?.users?.total
                ? `${Math.round(
                    (dashboardData.users.active / dashboardData.users.total) *
                      100
                  )}%`
                : "0%"
            }
            icon={<Percent size={24} className="text-orange-400" />}
          />
          <StatCard
            title="Total Notifications"
            value={dashboardData?.notifications?.total?.toLocaleString() || "0"}
            icon={<UserCheck size={24} className="text-orange-400" />}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2">
            <BarChartCard growthData={dashboardData?.users?.growth} />
          </div>
          <div className="lg:col-span-1">
            <OverallReports dashboardData={dashboardData} />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-5">
          <CampaignPerformance campaigns={campaigns} />
        </div>

        <div className="grid grid-cols-1 gap-5">
          <CampaignRequest
            campaigns={campaigns}
            navigate={navigate}
            onViewAll={handleViewAllCampaigns}
          />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
